import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'sme_bug_button_enabled'
const EVENT_NAME = 'sme-bug-button-visibility-changed'

function readStoredValue(): boolean {
  if (typeof window === 'undefined') return true
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === null) return true
    return raw === '1' || raw === 'true'
  } catch {
    return true
  }
}

function writeStoredValue(value: boolean): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }))
  } catch {
    /* swallow — quota exceeded / private mode */
  }
}

/**
 * `[enabled, setEnabled, toggle]`. Persists to localStorage and broadcasts
 * a custom event so multiple consumers of the hook (AppShell pill +
 * SmeFeedbackOverlay) stay in sync within the same tab. Falls back to ON
 * when storage is unavailable.
 */
export function useBugButtonVisibility(): [boolean, (value: boolean) => void, () => void] {
  const [enabled, setEnabledState] = useState<boolean>(readStoredValue)

  useEffect(() => {
    const onChange = (event: Event) => {
      if (event instanceof CustomEvent && typeof event.detail === 'boolean') {
        setEnabledState(event.detail)
      } else {
        setEnabledState(readStoredValue())
      }
    }
    window.addEventListener(EVENT_NAME, onChange)
    // StorageEvent fires across tabs — keep those in sync too.
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setEnabledState(readStoredValue())
    }
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(EVENT_NAME, onChange)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const setEnabled = useCallback((value: boolean) => {
    writeStoredValue(value)
    setEnabledState(value)
  }, [])

  const toggle = useCallback(() => {
    setEnabled(!readStoredValue())
  }, [setEnabled])

  return [enabled, setEnabled, toggle]
}
